import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, User, Mail, Phone, MapPin, Calendar, Globe, Shield, Briefcase, Hash, Euro } from 'lucide-react';
import { AddressDto, LegalEntityDto, NaturalPersonDto } from 'api/actor';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { InfoItem } from 'components/display/InfoItem';
import { useAppSelector } from 'app/hooks';
import { selectReferentialLabelsByIds } from 'features/referentials/selectors/selectReferentialIdByCode';
import { ReferentialKey } from 'api/referential';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';


export const isLegalEntity = (person: LegalEntityDto | NaturalPersonDto | undefined): person is LegalEntityDto => {
  if (!person) return false;

  const hasCompanyFields = 'companyName' in person &&
    'legalFormId' in person;

  const lacksPersonalFields = !('firstName' in person && 'lastName' in person);

  return hasCompanyFields && lacksPersonalFields;
};

export const formatAddress = (address: AddressDto | null | undefined): string => {
  if (!address) return 'N/A';

  const parts = [
    address.line1,
    address.line2,
    address.line3,
    [address.zipCode, address.city].filter(Boolean).join(' '),
    address.country
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(date));
};

export const NaturalPersonView: React.FC<{ person: NaturalPersonDto; withLinks?: boolean }> = ({ person, withLinks = true }) => {
  const navigate = useNavigate();
  const countryLabels = useAppSelector(state =>
    selectReferentialLabelsByIds(state, ReferentialKey.Country, [
      person.birthCountryId,
      person.taxResidenceCountryId,
      person.nationalityId,
    ])
  );

  const nationalityLabel = useAppSelector(state =>
    selectReferentialLabelsByIds(state, ReferentialKey.Nationality, [
      person.nationalityId,
    ])
  );

  const [nationality] = nationalityLabel;
  const [birthCountry, taxResidenceCountry] = countryLabels;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 pb-6 border-b border-primary/20">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <User className="w-8 h-8 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          {person.id && withLinks ? (
            <Link
              to={`/mes-clients/${person.id}`}
              className="text-2xl font-bold text-gray-900 truncate hover:text-primary transition-colors"
            >
              {person.firstName} {person.lastName}
            </Link>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900 truncate">
              {person.firstName} {person.lastName}
            </h2>
          )}
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            {person.companyRole && (
              <span className="text-primary font-medium">{person.companyRole}</span>
            )}
            {person.email && (
              <span className="text-gray-600 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {person.email}
              </span>
            )}
            {person.phone && (
              <span className="text-gray-600 flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {person.phone}
              </span>
            )}
          </div>
        </div>
        {person.id && withLinks && (
          <Button
            color='slate'
            round
            onClick={() => navigate(`/mes-clients/${person.id}`)}
          >
            <EyeIcon />
          </Button>
        )}
      </div>

      <div>
        <CardsSection title="Informations de l'assuré">
          <div className='grid grid-cols-2 divide-x'>
            <div className='pr-6'>
              <InfoItem
                icon={<MapPin />}
                label="Adresse"
                value={formatAddress(person.address)}
              />
              <InfoItem
                icon={<Calendar />}
                label="Date de naissance"
                value={formatDate(person.birthDate)}
              />
              <InfoItem
                icon={<Shield />}
                label="Personne Politiquement Exposée (PPE)"
                value={person.isPPE ? "Oui" : "Non"}
              />
              {person.isFPE !== undefined && (
                <InfoItem
                  icon={<Shield />}
                  label="Famille/Proche PPE (FPE)"
                  value={person.isFPE ? "Oui" : "Non"}
                />
              )}
            </div>
            <div className='pl-6'>
              <InfoItem
                icon={<Globe />}
                label="Nationalité"
                value={nationality}
              />
              <InfoItem
                icon={<Globe />}
                label="Pays de naissance"
                value={birthCountry}
              />
              <InfoItem
                icon={<Globe />}
                label="Résidence fiscale"
                value={taxResidenceCountry}
              />
            </div>
          </div>
        </CardsSection>
      </div>
    </div>
  );
};

/**
 * Fiche d'une personne morale sans son en-tête : informations légales, puis la section
 * réglementaire et les personnes associées seulement quand elle a quelque chose à montrer.
 */
export const LegalEntityDetails: React.FC<{ entity: LegalEntityDto }> = ({ entity }) => {
  const [legalFormLabel] = useAppSelector(state =>
    selectReferentialLabelsByIds(state, ReferentialKey.LegalForm, [entity.legalFormId ?? ''])
  );
  const hasRegulatoryInfos = Boolean(entity.oriasNumber || entity.rcp || entity.naturalPersons?.length);

  return (
    <div className="space-y-6">
      <div>
        <CardsSection title="Informations légales">
          <div className='grid grid-cols-2 divide-x'>
            <div className='pr-6'>
              <InfoItem
                icon={<Hash />}
                label="Numéro RCS"
                value={entity.rcsNumber}
              />
              <InfoItem
                icon={<MapPin />}
                label="Ville RCS"
                value={entity.rcsCity}
              />
              <InfoItem
                icon={<Euro />}
                label="Capital social"
                value={entity.capital != null ? `${entity.capital.toLocaleString('fr-FR')} €` : null}
              />
            </div>
            <div className='pl-6'>
              <InfoItem
                icon={<Briefcase />}
                label="Forme juridique"
                value={legalFormLabel ?? entity.legalFormId}
              />
              <InfoItem
                icon={<MapPin />}
                label="Adresse complète"
                value={formatAddress(entity.address)}
              />
            </div>
          </div>
        </CardsSection>
      </div>

      {hasRegulatoryInfos && (
      <div className="grid grid-cols-1">
        <CardsSection title="Informations réglementaires & Personnes associées">
          {entity.oriasNumber && (
            <InfoItem
              icon={<Hash />}
              label="Numéro ORIAS"
              value={entity.oriasNumber}
            />
          )}
          {entity.rcp && (
            <InfoItem
              icon={<Shield />}
              label="RCP"
              value={entity.rcp}
            />
          )}
          {entity.naturalPersons.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Personnes associées ({entity.naturalPersons.length})
              </h4>
              <div className="space-y-2">
                {entity.naturalPersons.map((person, index) => (
                  <div key={person.id || index} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900">
                      {person.firstName} {person.lastName}
                    </p>
                    {person.companyRole && (
                      <p className="text-sm text-primary">{person.companyRole}</p>
                    )}
                    {person.email && (
                      <p className="text-sm text-gray-600">{person.email}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardsSection>
      </div>
      )}
    </div>
  );
};

export const LegalEntityView: React.FC<{ entity: LegalEntityDto; withLinks?: boolean }> = ({ entity, withLinks = true }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 pb-6 border-b border-primary/20">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-8 h-8 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          {entity.id && withLinks ? (
            <Link
              to={`/mes-clients/${entity.id}`}
              className="text-2xl font-bold text-gray-900 truncate hover:text-primary transition-colors"
            >
              {entity.companyName}
            </Link>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900 truncate">{entity.companyName}</h2>
          )}
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="text-primary font-medium">Entreprise</span>
            {entity.registrationNumber && (
              <span className="text-gray-600 flex items-center gap-1">
                <Hash className="w-4 h-4" />
                SIRET: {entity.registrationNumber}
              </span>
            )}
            {entity.email && (
              <span className="text-gray-600 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {entity.email}
              </span>
            )}
            {entity.phone && (
              <span className="text-gray-600 flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {entity.phone}
              </span>
            )}
          </div>
        </div>
        {entity.id && withLinks && (
          <Button
            color='slate'
            round
            onClick={() => navigate(`/mes-clients/${entity.id}`)}
          >
            <EyeIcon />
          </Button>
        )}
      </div>

      <LegalEntityDetails entity={entity} />
    </div>
  );
};

interface PersonOverviewProps {
  person: LegalEntityDto | NaturalPersonDto;
  /** Links the person to its client page (default true); off where that page is out of reach. */
  withLinks?: boolean;
}

const PersonOverview: React.FC<PersonOverviewProps> = ({ person, withLinks = true }) => {
  return (
    <div>
      {isLegalEntity(person) ? (
        <LegalEntityView entity={person} withLinks={withLinks} />
      ) : (
        <NaturalPersonView person={person} withLinks={withLinks} />
      )}
    </div>
  );
};

export default PersonOverview;
